import axios from "axios";
import store from "../../../../globals";
import coursePlanService from "../../../../services/v2/coursePlan.service";
import { CoursePlanDTO } from "../../../../models/CoursePlanDTO";

/*
	Cursos con sus Módulos dentro (Horarios Claros, 2026-11-01). Todo lo que la pantalla lee usa los endpoints de siempre:
	v1 /courses (con sus módulos, kits y maestras), v1 /levels (para contar los horarios vivos), v1 /inventory (los kits del
	plantel) y v2 _plan (el precio). Guardar sigue siendo el mismo v1 de las pantallas viejas (CoursesService / CourseLevelsService).
*/

const V1 = process.env.REACT_APP_API_URL + "/admin/campus/";
const cabeceras = () => ({ "Content-Type": "application/json", Accept: "application/json", Authorization: "Bearer " + store.getState().sessionToken.value });
const lista = (data: any): any[] => (Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : []);

/** Curso con sus módulos, tal como lo trae el servidor (expand=4: etiquetas, categoría, modalidad, estado, módulos con kits y maestras). */
export const cargarCursos = async (campusId: number): Promise<any[]> =>
	lista((await axios.get(`${V1}${campusId}/courses?expand=4`, { headers: cabeceras() })).data);

const hoyISO = (): string => {
	const d = new Date();
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/** Horarios vivos por módulo: estado Activo y que no haya terminado (los mismos que Horarios llama En Curso y Por Empezar). */
export const cargarHorariosVivos = async (campusId: number): Promise<{ [levelId: number]: number }> => {
	const hoy = hoyISO();
	const niveles = lista((await axios.get(`${V1}${campusId}/levels?expand=1`, { headers: cabeceras() })).data);
	const conteo: { [levelId: number]: number } = {};
	niveles.forEach((n: any) => {
		conteo[n.id] = (n.schedules || []).filter((h: any) => Number(h.status_id) === 1 && String(h.end_date || "9999") >= hoy).length;
	});
	return conteo;
};

export interface KitOpcion { id: number; name: string; sku: string | null }

/** Los kits del plantel (artículos tipo Kit de módulo, activos): la lista de donde se ligan a un módulo. */
export const cargarKits = async (campusId: number): Promise<KitOpcion[]> =>
	lista((await axios.get(`${V1}${campusId}/inventory?typeId=1&statusId=1`, { headers: cabeceras() })).data)
		.map((a: any) => ({ id: Number(a.id), name: String(a.name || ""), sku: a.sku ?? null }))
		.sort((a, b) => a.name.localeCompare(b.name, "es"));

export interface PrecioCurso {
	cargado: boolean;
	porHorario: boolean;
	meses: number | null;
	mensualidad: number | null;
	inscripcion: number | null;
}

// Precio por curso: el plan se pide de a uno (no hay un listado con precios) y se guarda un rato para no repetirlo al volver.
const cachePrecios: { [clave: string]: { en: number; v: PrecioCurso } } = {};
const VIGENCIA_MS = 5 * 60 * 1000;

export const olvidarPrecio = (campusId: number, courseId: number) => { delete cachePrecios[campusId + "_" + courseId]; };

export const precioDeCurso = async (campusId: number, courseId: number): Promise<PrecioCurso> => {
	const clave = campusId + "_" + courseId;
	const previo = cachePrecios[clave];
	if (previo && Date.now() - previo.en < VIGENCIA_MS) return previo.v;
	let v: PrecioCurso;
	try {
		const d: CoursePlanDTO = (await coursePlanService.getPlan(campusId, courseId)).data;
		v = {
			cargado: true, porHorario: !!d.scheduleBilled, meses: d.plan ? d.plan.billableMonths : null,
			mensualidad: d.plan ? d.plan.monthlyPrice : null, inscripcion: d.effective?.subscriptionListPrice ?? null
		};
	} catch {
		v = { cargado: true, porHorario: false, meses: null, mensualidad: null, inscripcion: null };
	}
	cachePrecios[clave] = { en: Date.now(), v };
	return v;
};
